import { createColumnHelper } from '@tanstack/react-table';
import { string } from 'fp-ts';
import { lt, useLocalizeText } from '@mews/framework';
import { RoutingRuleAssignmentTargetType } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { RoutingRuleListTableRowData } from '../types';
import { ActionCell } from '../cells/ActionCell';
import { ConditionsCell } from '../cells/ConditionsCell';
import { ServicesCell } from '../cells/ServicesCell';
import { TimingCell } from '../cells/TimingCell';
import { TargetBillCell } from '../cells/TargetBillCell';
import { NameCell } from '../cells/NameCell';

const columnHelper = createColumnHelper<RoutingRuleListTableRowData>();

export const useRoutingRulesTableColumns = () => {
    const localizedText = useLocalizeText();
    return [
        columnHelper.accessor('name', {
            id: 'name',
            enableSorting: true,
            header: () => lt('Name'),
            cell: (props) => <NameCell name={props.getValue()} />,
            meta: {
                width: 310,
            },
        }),
        columnHelper.accessor('timing', {
            id: 'timing',
            enableSorting: true,
            header: () => lt('Timing'),
            cell: ({ getValue, row }) => (
                <TimingCell timing={getValue()} processingStartOffset={row.original.processingStartOffset} />
            ),
        }),
        columnHelper.accessor('conditions', {
            id: 'conditions',
            header: () => lt('Conditions'),
            cell: (props) => <ConditionsCell conditions={props.getValue()} />,
        }),
        columnHelper.accessor('services', {
            id: 'services',
            header: () => lt('Service'),
            cell: (props) => <ServicesCell services={props.getValue()} />,
        }),
        columnHelper.accessor('targetBill', {
            id: 'targetBill',
            enableSorting: true,
            header: () => lt('TargetBill'),
            cell: (props) => <TargetBillCell targetBill={props.getValue()} />,
            sortingFn: (rowA, rowB) => {
                const getTargetTitle = (targetType: RoutingRuleAssignmentTargetType) => {
                    const textKey =
                        targetType === RoutingRuleAssignmentTargetType.CompanyAsOwner
                            ? TextKey.RoutingRulesCompanyBill
                            : TextKey.RoutingRulesGuestBill;
                    return localizedText({ textKey });
                };

                return string.Ord.compare(
                    getTargetTitle(rowA.original.targetBill.targetType),
                    getTargetTitle(rowB.original.targetBill.targetType)
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: ({ row }) => <ActionCell routingRuleId={row.original.id} routingRuleName={row.original.name} />,
            meta: {
                align: 'end',
                width: 60,
            },
        }),
    ];
};
